import React from 'react';
import {
  authenticateWithPasskey,
  login,
  loginWithGoogle,
  LoginResult,
  signIn,
} from '../../../services/auth';
import { Input, PasswordInput } from '../../../components/Form';
import { Box, Link, Text } from 'rebass';
import Button from '../../../components/Button/Button';
import { useParams } from 'react-router-dom';
import AuthForm from '../components/AuthForm';
import ExternalAuthButtons from './ExternalAuthButtons';
import {
  checkHeading,
  codeSentMessage,
  resendLabel,
  switchLabel,
} from '@ecosconnect/core/src/auth/client';
import type { Challenge } from '@ecosconnect/core/src/auth/types';
import { takeLoginPath } from './loginRedirect';
import { useOnMountUnsafe } from '@/helpers/useOnMountUnsafe';
import {
  browserSupportsWebAuthnAutofill,
  WebAuthnError,
} from '@simplewebauthn/browser';
import theme from '@/theme';
import Spinner from '@/components/Spinner/Spinner';
import useVerificationChallenge from '@/hooks/useVerificationChallenge';
import CodeInput from '@/components/Security/CodeInput';

/**
 * @component for login form
 */
const LoginForm: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [pw, setPW] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [mfaChallenge, setMfaChallenge] = React.useState<Challenge>();

  const { authIssuer } = useParams<{ authIssuer?: string }>();
  const [isLoading, setIsLoading] = React.useState(!!authIssuer);

  const showError = (err: unknown) => {
    setErrorMessage((err as Error).message);
    setIsLoading(false);
  };

  const showLoginResult = (result: LoginResult) => {
    if (result.kind === 'challenge') {
      setMfaChallenge(result.challenge);
      setErrorMessage('');
      setIsLoading(false);
    }
  };

  useOnMountUnsafe(() => {
    if (authIssuer === 'google') {
      const searchParams = new URLSearchParams(window.location.search);
      const authCode = searchParams.get('code');
      const returnedState = searchParams.get('state');
      const expectedState = sessionStorage.getItem('google-oauth-state');

      sessionStorage.removeItem('google-oauth-state');
      // Restore ?redirect= so goToHomePage sends the user back where they started.
      window.history.replaceState(null, '', takeLoginPath(sessionStorage));

      if (
        !authCode ||
        !returnedState ||
        !expectedState ||
        returnedState !== expectedState
      ) {
        setErrorMessage('Sign-in failed: invalid or missing state parameter.');
        setIsLoading(false);
        return;
      }

      loginWithGoogle({ code: authCode })
        .then(showLoginResult)
        .catch(showError);
    }
  });

  // Handles when form is submitted
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      showLoginResult(await login(email, pw));
    } catch (err) {
      showError(err);
    }
  };

  // When input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    if (e.target.name === 'email') {
      setEmail(e.target.value);
    } else {
      setPW(e.target.value);
    }
  };

  useOnMountUnsafe(() => {
    browserSupportsWebAuthnAutofill().then(async (supports) => {
      if (supports) {
        try {
          await authenticateWithPasskey(true);
        } catch (err) {
          if (
            err instanceof WebAuthnError &&
            err.code === 'ERROR_CEREMONY_ABORTED'
          ) {
            return;
          } else {
            console.error(err);
          }
        }
      }
    });
  });

  if (mfaChallenge) {
    return (
      <MfaLoginForm
        challenge={mfaChallenge}
        onRestart={(message) => {
          setMfaChallenge(undefined);
          setErrorMessage(message);
        }}
      />
    );
  }

  return (
    <AuthForm onSubmit={handleSubmit}>
      <Input
        id="input-email"
        type="email"
        aria-label={`Email`}
        placeholder="Email"
        mt={5}
        padding={5}
        name="email"
        value={email}
        onChange={handleInputChange}
        disabled={isLoading}
        autoComplete="username webauthn"
      />
      <PasswordInput
        id="input-password"
        name="password"
        aria-label={`Password`}
        placeholder="Password"
        mt={3}
        padding={5}
        value={pw}
        onChange={handleInputChange}
        showPassword={showPassword}
        toggleShowPassword={() => setShowPassword(!showPassword)}
        disabled={isLoading}
      />

      <Box textAlign="right" mt={1}>
        <Link
          fontSize={0}
          color={theme.colors.secondary}
          pr={2}
          href={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
        >
          Forgot Password?
        </Link>
      </Box>

      {errorMessage && !isLoading && (
        <Text variant="subheading" mt={2} sx={{ color: '#D9534F' }}>
          {errorMessage}
        </Text>
      )}
      <Button
        width="100%"
        mt={errorMessage ? 2 : 6}
        justifyContent="center"
        paddingY={3}
        type="submit"
        variant="primary"
      >
        Login
      </Button>
      <ExternalAuthButtons onLogin={showLoginResult} onError={showError} />
      {isLoading && <LoadingOverlay />}
    </AuthForm>
  );
};

type MfaLoginFormProps = {
  challenge: Challenge;
  onRestart: (message: string) => void;
};

const MfaLoginForm: React.FC<MfaLoginFormProps> = ({
  challenge,
  onRestart,
}) => {
  const [verified, setVerified] = React.useState(false);
  const verification = useVerificationChallenge({
    purpose: 'login',
    challenge,
    onVerified: async (result) => {
      setVerified(true);
      await signIn(result);
    },
    onExit: onRestart,
  });
  const { pending, error } = verification;

  return (
    <AuthForm
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        verification.verify();
      }}
    >
      <Text mt={5} variant="heading3">
        {checkHeading(verification.challenge.method)}
      </Text>
      <Text mt={2}>{codeSentMessage(verification.challenge)}</Text>
      <CodeInput
        challenge={verification}
        id="input-verification-code"
        aria-label="Verification code"
        mt={4}
        padding={5}
      />
      {error && (
        <Text
          variant="subheading"
          mt={2}
          sx={{ color: '#D9534F' }}
          role="alert"
        >
          {error}
        </Text>
      )}
      <Button
        width="100%"
        mt={error ? 2 : 5}
        justifyContent="center"
        paddingY={3}
        type="submit"
        variant="primary"
        disabled={!verification.canSubmit}
      >
        Verify
      </Button>
      <Button
        width="100%"
        mt={2}
        justifyContent="center"
        paddingY={3}
        type="button"
        variant="outline"
        disabled={!verification.canResend}
        onClick={verification.resend}
      >
        {resendLabel(
          verification.secondsUntilResend,
          verification.resendBlocked
        )}
      </Button>
      {verification.alternative && (
        <Box textAlign="center" mt={3}>
          <Button
            variant="link"
            color="secondary"
            sx={{ textDecoration: 'underline' }}
            onClick={verification.switchMethod}
            disabled={!!pending}
          >
            {switchLabel(verification.alternative)}
          </Button>
        </Box>
      )}
      <Box textAlign="center" mt={3}>
        <Button
          variant="link"
          color="secondary"
          sx={{ textDecoration: 'underline' }}
          onClick={() => onRestart('')}
        >
          Back to sign in
        </Button>
      </Box>
      {(pending || verified) && <LoadingOverlay />}
    </AuthForm>
  );
};

const LoadingOverlay: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(2px)',
      zIndex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <Spinner size={65} color={theme.colors.primary} backgroundColor="#eee" />
  </div>
);

export default LoginForm;
